import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Alert
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon
} from '@mui/icons-material';

export default function SocialPostComposer({ post, onSave, onCancel }) {
  const [platform, setPlatform] = useState('facebook');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [saleId, setSaleId] = useState('');

  useEffect(() => {
    if (post) {
      setPlatform(post.platform);
      setContent(post.content);
      setImageUrl(post.image_url || '');
      setSaleId(post.sale_id || '');
    }
  }, [post]);

  const handleSave = () => {
    if (!content) {
      alert('Please enter post content');
      return;
    }

    const characterLimit = getCharacterLimit();
    if (content.length > characterLimit) {
      alert(`Content exceeds ${characterLimit} character limit for ${platform}`);
      return;
    }

    onSave({
      platform,
      content,
      image_url: imageUrl || null,
      sale_id: saleId || null
    });
  };

  const getCharacterLimit = () => {
    switch (platform) {
      case 'twitter': return 280;
      case 'facebook': return 63206;
      case 'instagram': return 2200;
      default: return 1000;
    }
  };

  const getPlatformIcon = (platformName) => {
    switch (platformName) {
      case 'facebook': return <FacebookIcon />;
      case 'instagram': return <InstagramIcon />;
      case 'twitter': return <TwitterIcon />;
      default: return null;
    }
  };

  const characterLimit = getCharacterLimit();
  const remainingChars = characterLimit - content.length;

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Platform</InputLabel>
            <Select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              label="Platform"
            >
              <MenuItem value="facebook">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FacebookIcon /> Facebook
                </Box>
              </MenuItem>
              <MenuItem value="instagram">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InstagramIcon /> Instagram
                </Box>
              </MenuItem>
              <MenuItem value="twitter">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TwitterIcon /> Twitter
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Post Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
            helperText={`${remainingChars} characters remaining`}
            error={remainingChars < 0}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Image URL (optional)"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            type="number"
            label="Related Sale ID (optional)"
            value={saleId}
            onChange={(e) => setSaleId(e.target.value)}
          />
        </Grid>

        {remainingChars < 0 && (
          <Grid item xs={12}>
            <Alert severity="error">
              Content exceeds the {characterLimit} character limit for {platform}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save Post
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
